import {
  format,
  formatDistance,
  formatDistanceToNow,
  isValid,
  parse,
} from 'date-fns';
import { formatClock } from '@/components/builder/clock.util';
import type { BuilderStatus } from '@/components/builder/status-badge.component';
import { zoneCity } from '@/components/builder/timezone.util';
import {
  LAST_DAY_OF_MONTH,
  ordinal,
} from '@/containers/playbooks/custom-builder/month-day';
import { TRIGGER_KIND_LABEL } from '@/containers/playbooks/custom-builder/node-metadata';
import {
  pollMinutes,
  pollMinutesWords,
} from '@/containers/playbooks/custom-builder/poll-interval';
import type {
  CustomPlaybookDto,
  TriggerSchedule,
} from '@/types/custom-playbooks';
import { apiTime } from './library.util';
import type {
  AgentDto,
  AgentStatus,
  AgentTriggerKind,
} from '@/types/gtm-agents';

/**
 * How an agent reads in the index and on its page: its status in the builder
 * vocabulary, what it does, and what starts it.
 */

export type AgentStatusFilter = 'all' | AgentStatus;

const STATUS_LABEL: Record<AgentStatus, BuilderStatus> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
};

export function agentStatus(agent: Pick<AgentDto, 'status'>): BuilderStatus {
  return STATUS_LABEL[agent.status] ?? 'Draft';
}

/** A play on its own: Draft until it has ever been saved, then on or off. */
export function playStatus(
  play: Pick<CustomPlaybookDto, 'version' | 'isEnabled'>
): BuilderStatus {
  if (play.version === 0) return 'Draft';
  return play.isEnabled ? 'Active' : 'Off';
}

export function matchesStatus(
  agent: Pick<AgentDto, 'status'>,
  filter: AgentStatusFilter
): boolean {
  return filter === 'all' || agent.status === filter;
}

/**
 * An agent the plays backfill wrapped around an empty draft play (D17): set
 * up and greeted in the same instant, every play of it only an unconfigured trigger and
 * made before the agent was, never run, nothing pending and nothing added
 * since. The index leaves it out; the agent and its plays stay as they are.
 *
 * A duplicate is also set up and greeted in one instant, but its plays are
 * copied after it, so the age check keeps a copy of an empty agent listed.
 */
type IndexPlay = Pick<CustomPlaybookDto, 'id' | 'graph' | 'createdAt'>;

function madeBefore(play: IndexPlay, stamp: string): boolean {
  if (!play.createdAt) return true;
  const made = apiTime(play.createdAt);
  return !Number.isNaN(made) && made < apiTime(stamp);
}

function isEmptyWrapped(
  agent: AgentDto,
  plays: ReadonlyMap<string, IndexPlay>
): boolean {
  if (!agent.greetedAt || agent.greetedAt !== agent.setupCompletedAt)
    return false;
  // Every edit (rename, knowledge, connections, delivery) bumps the version.
  if (
    agent.version !== 1 ||
    !agent.playIds.length ||
    agent.status === 'active' ||
    agent.lastRunAt ||
    agent.pendingProposalId ||
    agent.instructions?.trim() ||
    agent.skillRefs.length
  )
    return false;
  const greetedAt = agent.greetedAt;
  return agent.playIds.every((id) => {
    const play = plays.get(id);
    return (
      !!play &&
      madeBefore(play, greetedAt) &&
      play.graph.nodes.every(
        (node) =>
          node.type === 'trigger' && Object.keys(node.config ?? {}).length === 0
      )
    );
  });
}

/** The agents the index lists: all of them until the plays are known. */
export function indexAgents(
  agents: AgentDto[],
  plays: readonly IndexPlay[] | undefined
): AgentDto[] {
  if (!plays) return agents;
  const byId = new Map(plays.map((play) => [play.id, play]));
  return agents.filter((agent) => !isEmptyWrapped(agent, byId));
}

/** "09:00" as "9:00 AM". Anything unparseable comes back as it was. */
export function clockLabel(time: string | undefined): string {
  if (!time) return '';
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match || Number(match[1]) > 23) return time;
  return formatClock(time);
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The day a monthly schedule runs on: "on the 15th", "on the last day". */
function monthDayWords(dayOfMonth: number | undefined): string {
  if (dayOfMonth === LAST_DAY_OF_MONTH) return 'on the last day';
  return dayOfMonth ? 'on the ' + ordinal(dayOfMonth) : '';
}

/**
 * The schedule in words: "Every 5 minutes", "Weekly, Mon 9:00 AM",
 * "Monthly on the 15th at 9:00 AM", "First business day of each quarter at
 * 9:00 AM".
 */
export function scheduleWords(
  schedule: TriggerSchedule | null | undefined
): string {
  if (!schedule || schedule.frequency === 'continuous') {
    return pollMinutesWords(pollMinutes(schedule));
  }
  const at = clockLabel(schedule.time);
  const withAt = (words: string) => (at ? `${words} at ${at}` : words);
  switch (schedule.frequency) {
    case 'daily':
      return at ? `Daily, ${at}` : 'Daily';
    case 'weekdays':
      return at ? `Weekdays, ${at}` : 'Weekdays';
    case 'every_n_days': {
      const n = schedule.everyNDays ?? 2;
      return at ? `Every ${n} days, ${at}` : `Every ${n} days`;
    }
    case 'weekly': {
      const day = WEEKDAYS[schedule.weekday ?? 0] ?? WEEKDAYS[0];
      return at ? `Weekly, ${day} ${at}` : `Weekly, ${day}`;
    }
    case 'monthly': {
      if (schedule.firstBusinessDay)
        return withAt('First business day of each month');
      const day = monthDayWords(schedule.dayOfMonth);
      return withAt(day ? 'Monthly ' + day : 'Monthly');
    }
    case 'quarterly':
      return withAt('First business day of each quarter');
    default:
      return '';
  }
}

/** The schedule with the city its clock is in: "Weekly, Mon 9:00 AM, Toronto time". */
export function scheduleWithZone(
  schedule: TriggerSchedule | null | undefined
): string {
  const words = scheduleWords(schedule);
  if (!schedule || schedule.frequency === 'continuous' || !schedule.timezone)
    return words;
  return words + ', ' + zoneCity(schedule.timezone) + ' time';
}

/** A one time trigger's moment, as the agent stores it. */
export interface OneTimeConfig {
  /** 'YYYY-MM-DD' */
  date?: string;
  /** 'HH:MM' */
  time?: string;
  /** IANA zone the date and time are in. */
  timezone?: string;
}

/** "Once, Sep 30 9:00 AM, Toronto time". */
export function oneTimeWords(config: OneTimeConfig | undefined): string {
  const day = config?.date
    ? parse(config.date, 'yyyy-MM-dd', new Date())
    : null;
  const parts = [
    day && isValid(day) ? format(day, 'MMM d') : '',
    clockLabel(config?.time),
  ].filter(Boolean);
  const when = parts.length ? 'Once, ' + parts.join(' ') : 'Once';
  return config?.timezone
    ? when + ', ' + zoneCity(config.timezone) + ' time'
    : when;
}

/** What starts the agent, by name. */
export function triggerLabel(
  kind: AgentTriggerKind | null | undefined
): string {
  if (!kind) return 'Pick a trigger';
  if (kind === 'schedule') return 'Schedule';
  if (kind === 'one_time') return 'One time';
  return TRIGGER_KIND_LABEL[kind] ?? kind;
}

/** What the agent takes care of: the one line the index shows. */
export function agentSummary(agent: Pick<AgentDto, 'description'>): string {
  return agent.description?.trim() ?? '';
}

/** Which agent owns each play, by play id. A play no agent owns is absent. */
export function playOwners(
  agents: readonly Pick<AgentDto, 'id' | 'name' | 'playIds'>[]
): Map<string, Pick<AgentDto, 'id' | 'name' | 'playIds'>> {
  const owners = new Map<string, Pick<AgentDto, 'id' | 'name' | 'playIds'>>();
  for (const agent of agents)
    for (const playId of agent.playIds) owners.set(playId, agent);
  return owners;
}

/** "2 hours ago", "in 3 days". Blank for nothing or an unreadable date. */
export function timeAgo(
  iso: string | null | undefined,
  /** Measured from this time instead of the clock, when given. */
  now?: Date
): string {
  if (!iso) return '';
  const date = new Date(apiTime(iso));
  if (Number.isNaN(date.getTime())) return '';
  return now
    ? formatDistance(date, now, { addSuffix: true })
    : formatDistanceToNow(date, { addSuffix: true });
}
